import api from './api';

export interface SubscriptionDetails {
  currentPlan: 'trial' | 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'expired' | 'cancelled' | 'grace_period';
  startDate: string;
  endDate: string;
  nextBillingDate?: string;
  autoRenew: boolean;
  trialEndDate?: string;
  limits: {
    maxUsers: number;
    maxWarehouses: number;
    maxInventoryItems: number;
    maxPurchaseOrdersPerMonth: number;
    maxRetailBillsPerMonth: number;
  };
  features: string[];
  subscriptionHistory: SubscriptionHistory[];
}

export interface SubscriptionHistory {
  _id: string;
  plan: string;
  status: string;
  billingCycle: 'monthly' | 'annual';
  amount: number;
  currency: string;
  startDate: string;
  endDate: string;
  transactionType: string;
  transactionStatus: string;
  invoiceNumber?: string;
  createdAt: string;
}

export interface Plan {
  plan: string;
  pricing: {
    monthly: number;
    annual: number;
  };
  limits: {
    maxUsers: number;
    maxWarehouses: number;
    maxInventoryItems: number;
    maxPurchaseOrdersPerMonth: number;
    maxRetailBillsPerMonth: number;
  };
  features: string[];
}

export interface CreateSubscriptionDto {
  plan: 'starter' | 'professional' | 'enterprise';
  billingCycle: 'monthly' | 'annual';
  autoRenew?: boolean;
  paymentMethodId?: string;
  promoCode?: string;
}

export interface UpgradeSubscriptionDto {
  newPlan: 'starter' | 'professional' | 'enterprise';
  immediate?: boolean;
}

export interface CancelSubscriptionDto {
  reason?: string;
  immediate?: boolean;
}

const subscriptionService = {
  // Get current subscription details
  getCurrentSubscription: () =>
    api.get<SubscriptionDetails>('/subscriptions/current').then((res) => res.data),

  // Get available plans (public endpoint)
  getAvailablePlans: () =>
    api.get<Plan[]>('/subscriptions/plans').then((res) => res.data),

  // Create a new subscription
  createSubscription: (data: CreateSubscriptionDto) =>
    api.post('/subscriptions', data).then((res) => res.data),

  // Upgrade subscription
  upgradeSubscription: (data: UpgradeSubscriptionDto) =>
    api.post('/subscriptions/upgrade', data).then((res) => res.data),

  // Cancel subscription
  cancelSubscription: (data: CancelSubscriptionDto) =>
    api.post('/subscriptions/cancel', data).then((res) => res.data),

  // Confirm payment
  confirmPayment: (subscriptionId: string, transactionId: string, paymentGateway: string = 'razorpay') =>
    api.post(`/subscriptions/confirm-payment/${subscriptionId}`, {
      transactionId,
      paymentGateway,
    }).then((res) => res.data),

  // Renew subscription manually
  renewSubscription: () =>
    api.post('/subscriptions/renew').then((res) => res.data),

  // Check if a feature is enabled
  checkFeature: (feature: string) =>
    api.get<{ feature: string; enabled: boolean }>(`/subscriptions/check-feature/${feature}`).then((res) => res.data),

  // Helper: Check if subscription is active
  isSubscriptionActive: async (): Promise<boolean> => {
    try {
      const subscription = await subscriptionService.getCurrentSubscription();
      return subscription.status === 'active' || subscription.status === 'grace_period';
    } catch (error) {
      return false;
    }
  },

  // Helper: Get days remaining in trial
  getTrialDaysRemaining: async (): Promise<number> => {
    try {
      const subscription = await subscriptionService.getCurrentSubscription();
      if (subscription.currentPlan !== 'trial' || !subscription.trialEndDate) {
        return 0;
      }
      const now = new Date();
      const endDate = new Date(subscription.trialEndDate);
      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch (error) {
      return 0;
    }
  },

  // Helper: Get days until subscription expires
  getDaysUntilExpiry: async (): Promise<number> => {
    try {
      const subscription = await subscriptionService.getCurrentSubscription();
      if (!subscription.endDate) {
        return 0;
      }
      const now = new Date();
      const endDate = new Date(subscription.endDate);
      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch (error) {
      return 0;
    }
  },

  // Helper: Format plan name for display
  formatPlanName: (plan: string): string => {
    const planNames: Record<string, string> = {
      trial: 'Trial',
      starter: 'Starter',
      professional: 'Professional',
      enterprise: 'Enterprise',
    };
    return planNames[plan] || plan;
  },

  // Helper: Format feature name for display
  formatFeatureName: (feature: string): string => {
    return feature
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  // Helper: Get plan color for UI
  getPlanColor: (plan: string): string => {
    const colors: Record<string, string> = {
      trial: 'gray',
      starter: 'blue',
      professional: 'indigo',
      enterprise: 'purple',
    };
    return colors[plan] || 'gray';
  },

  // Helper: Get status badge color
  getStatusColor: (status: string): string => {
    const colors: Record<string, string> = {
      active: 'green',
      grace_period: 'yellow',
      expired: 'red',
      cancelled: 'gray',
    };
    return colors[status] || 'gray';
  },

  // Helper: Format currency
  formatCurrency: (amount: number, currency: string = 'INR'): string => {
    if (currency === 'INR') {
      return `₹${amount.toLocaleString('en-IN')}`;
    }
    return `${currency} ${amount}`;
  },
};

export default subscriptionService;
